/* Bulk Employee Import , CSV upload + preview + commit.
   Company Admin only. Two-pass flow: parse and validate preview, then execute.
*/
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { SectionHeader } from "../components/UI";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, X, ArrowRight, Copy, Users,
} from "lucide-react";

export default function EmployeesImport() {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("all");

  const shownRows = useMemo(() => {
    if (!preview) return [];
    if (filter === "all") return preview.rows;
    return preview.rows.filter((r) => r.status === filter);
  }, [preview, filter]);

  const canManage = user.role === "company_admin" || user.role === "super_admin";
  if (!canManage) {
    return (
      <div className="p-8 max-w-3xl mx-auto">
        <div className="data-cell p-8 text-center">
          <div className="eyebrow mb-2">Restricted</div>
          <div className="text-slate-700">Only Company Admin or Super Admin can bulk-import employees.</div>
        </div>
      </div>
    );
  }

  const onFile = (f) => {
    setPreview(null);
    setResults(null);
    setFile(f || null);
  };

  const runPreview = async () => {
    if (!file) return;
    setBusy(true); setResults(null);
    try {
      const fd = new FormData();
      fd.append("csv_file", file);
      const { data } = await api.post("/employees/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      toast.success(`Parsed ${data.summary.total} rows (${data.summary.create} new, ${data.summary.update} updates, ${data.summary.error} errors)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const runExecute = async () => {
    if (!file) return;
    setBusy(true);
    setResults(null);
    try {
      const fd = new FormData();
      fd.append("csv_file", file);
      const { data } = await api.post("/employees/import/execute", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResults(data);
      toast.success(`Import complete , ${data.summary.created} created, ${data.summary.updated} updated, ${data.summary.errored} errored`);
      // Deterministically scroll the results panel into view for humans and test bots.
      setTimeout(() => {
        document.querySelector("[data-testid='import-results']")
          ?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Execute failed");
    } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/employees/import/template");
      const blob = new Blob([data.content], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = data.filename || "prophr-employees-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Template download failed");
    }
  };

  const copyPasswords = async () => {
    if (!results) return;
    const lines = results.results
      .filter((r) => r.temporary_password)
      .map((r) => `${r.email},${r.temporary_password}`);
    if (!lines.length) return toast.error("No new passwords to copy.");
    try {
      await navigator.clipboard.writeText("email,temporary_password\n" + lines.join("\n"));
      toast.success(`Copied ${lines.length} credentials to clipboard`);
    } catch { toast.error("Clipboard blocked. Please copy manually."); }
  };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <SectionHeader
        title="Bulk Employee Import"
        subtitle="Upload a CSV to seed employees, departments, and manager chains in one pass. Preview first, commit second."
        right={<Link to="/users" className="btn-ghost text-sm" data-testid="import-back-link"><Users size={14}/> User directory</Link>}
      />

      {/* Uploader + template */}
      <div className="data-cell p-6 mb-6" data-testid="import-uploader">
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex-1 min-w-[280px] cursor-pointer border-2 border-dashed border-slate-300 rounded p-6 text-center hover:border-indigo-500 transition" data-testid="import-drop">
            <input
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => onFile(e.target.files?.[0])}
              data-testid="import-file"
            />
            <FileSpreadsheet size={28} className="mx-auto text-slate-400" strokeWidth={1.5} />
            <div className="mt-2 text-sm text-slate-700">
              {file ? (
                <span className="font-medium text-slate-900">{file.name}</span>
              ) : (
                <span>Click to select a CSV, or drag one here.</span>
              )}
            </div>
            <div className="mt-1 text-[11px] font-mono text-slate-500">
              Required columns: email, name, role. Optional: department, title, manager_email, phone, password.
            </div>
          </label>
          <div className="flex flex-col gap-2 min-w-[180px]">
            <button className="btn-ghost text-xs" onClick={downloadTemplate} data-testid="import-template">
              <Download size={13}/> Download template
            </button>
            <button
              className="btn-primary text-xs"
              disabled={!file || busy}
              onClick={runPreview}
              data-testid="import-preview"
            >
              <Upload size={13}/> {busy ? "Parsing..." : "Preview"}
            </button>
          </div>
        </div>
      </div>

      {/* Preview summary + rows */}
      {preview && (
        <div className="data-cell p-6 mb-6" data-testid="import-preview-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex flex-wrap gap-2">
              <Chip active={filter === "all"} onClick={() => setFilter("all")} testid="filter-all">All {preview.summary.total}</Chip>
              <Chip active={filter === "create"} onClick={() => setFilter("create")} testid="filter-create" tone="emerald">Create {preview.summary.create}</Chip>
              <Chip active={filter === "update"} onClick={() => setFilter("update")} testid="filter-update" tone="indigo">Update {preview.summary.update}</Chip>
              <Chip active={filter === "error"} onClick={() => setFilter("error")} testid="filter-error" tone="red">Errors {preview.summary.error}</Chip>
            </div>
            <button
              className="btn-primary text-sm"
              disabled={busy}
              onClick={runExecute}
              data-testid="import-execute"
            >
              <ArrowRight size={13}/> {busy ? "Committing..." : `Execute import (${preview.summary.create + preview.summary.update})`}
            </button>
          </div>

          {preview.summary.departments_new?.length > 0 && (
            <div className="mb-3 text-xs text-slate-600">
              <span className="eyebrow mr-2">Departments in this file</span>
              {preview.summary.departments_new.map((d) => (
                <span key={d} className="inline-block bg-slate-100 rounded px-2 py-0.5 mr-1 font-mono text-[10px]">{d}</span>
              ))}
            </div>
          )}

          <div className="border border-slate-200 rounded overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2 eyebrow">Line</th>
                  <th className="px-3 py-2 eyebrow">Email</th>
                  <th className="px-3 py-2 eyebrow">Name</th>
                  <th className="px-3 py-2 eyebrow">Role</th>
                  <th className="px-3 py-2 eyebrow">Department</th>
                  <th className="px-3 py-2 eyebrow">Manager</th>
                  <th className="px-3 py-2 eyebrow">Status</th>
                  <th className="px-3 py-2 eyebrow">Notes</th>
                </tr>
              </thead>
              <tbody>
                {shownRows.map((r) => (
                  <tr key={r.line} className="border-b border-slate-100 last:border-b-0" data-testid={`preview-row-${r.line}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.line}</td>
                    <td className="px-3 py-2 truncate max-w-[200px]">{r.email}</td>
                    <td className="px-3 py-2 truncate max-w-[160px]">{r.name}</td>
                    <td className="px-3 py-2 text-xs">{r.role}</td>
                    <td className="px-3 py-2 text-xs">{r.department || <span className="text-slate-400">,</span>}</td>
                    <td className="px-3 py-2 text-xs truncate max-w-[180px]">{r.manager_email || <span className="text-slate-400">,</span>}</td>
                    <td className="px-3 py-2"><StatusChip status={r.status} /></td>
                    <td className="px-3 py-2 text-xs text-red-700">
                      {r.errors?.map((e, i) => <div key={i} className="inline-flex items-center gap-1"><AlertTriangle size={11}/> {e}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Results after execute */}
      {results && (
        <div className="data-cell p-6" data-testid="import-results">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="text-slate-800">
              <div className="text-2xl font-semibold">Import complete</div>
              <div className="text-sm text-slate-600 mt-1">
                {results.summary.created} created · {results.summary.updated} updated · {results.summary.errored} errored
              </div>
            </div>
            <div className="flex gap-2">
              <button className="btn-ghost text-xs" onClick={copyPasswords} data-testid="import-copy-passwords">
                <Copy size={13}/> Copy one-time passwords
              </button>
              <Link to="/users" className="btn-primary text-xs">
                <Users size={13}/> Open user directory
              </Link>
            </div>
          </div>
          <div className="border border-slate-200 rounded overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                <tr className="text-left">
                  <th className="px-3 py-2 eyebrow">Line</th>
                  <th className="px-3 py-2 eyebrow">Email</th>
                  <th className="px-3 py-2 eyebrow">Status</th>
                  <th className="px-3 py-2 eyebrow">One-time password</th>
                  <th className="px-3 py-2 eyebrow">Notes</th>
                </tr>
              </thead>
              <tbody>
                {results.results.map((r) => (
                  <tr key={r.line} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-500">{r.line}</td>
                    <td className="px-3 py-2 truncate max-w-[220px]">{r.email}</td>
                    <td className="px-3 py-2"><StatusChip status={r.errors?.length ? "error" : r.status} /></td>
                    <td className="px-3 py-2 font-mono text-[11px]">
                      {r.temporary_password ? r.temporary_password : <span className="text-slate-400">,</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-red-700">
                      {r.errors?.map((e, i) => <div key={i} className="inline-flex items-center gap-1"><AlertTriangle size={11}/> {e}</div>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-3 text-[11px] font-mono text-slate-500 inline-flex items-center gap-1">
            <CheckCircle2 size={12} className="text-emerald-600"/> Employees can change their password from Settings on first login.
          </div>
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, tone = "slate", children, testid }) {
  const bg = active
    ? { slate: "bg-slate-900 text-white", emerald: "bg-emerald-600 text-white", indigo: "bg-indigo-700 text-white", red: "bg-red-600 text-white" }[tone]
    : "bg-slate-100 text-slate-700 hover:bg-slate-200";
  return (
    <button onClick={onClick} className={"text-xs px-3 py-1.5 rounded font-medium transition " + bg} data-testid={testid}>
      {children}
    </button>
  );
}

function StatusChip({ status }) {
  const map = {
    create: { txt: "Create", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
    update: { txt: "Update", cls: "bg-indigo-50 text-indigo-800 border-indigo-200" },
    error:  { txt: "Error",  cls: "bg-red-50 text-red-800 border-red-200" },
  };
  const s = map[status] || { txt: status, cls: "bg-slate-50 text-slate-700 border-slate-200" };
  return (
    <span className={"inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded border " + s.cls}>
      {status === "create" && <CheckCircle2 size={10}/>}
      {status === "update" && <ArrowRight size={10}/>}
      {status === "error"  && <X size={10}/>}
      {s.txt}
    </span>
  );
}
